import React, { useState } from "react";
import { CheckCircle, AlertTriangle, XCircle, ChevronDown, Search } from "lucide-react";

// 主题变量化：状态/严重度色映射到语义变量，透明度用 color-mix 派生
const STATUS_CONFIG = {
  pass: { icon: CheckCircle, color: "var(--color-success)", label: "通过", bg: "color-mix(in srgb, var(--color-success) 8%, transparent)" },
  warning: { icon: AlertTriangle, color: "var(--color-warning)", label: "有疑点", bg: "color-mix(in srgb, var(--color-warning) 8%, transparent)" },
  fail: { icon: XCircle, color: "var(--color-error)", label: "有问题", bg: "color-mix(in srgb, var(--color-error) 8%, transparent)" },
};

const SEVERITY_COLOR = {
  high: "var(--color-error)",
  medium: "var(--color-warning)",
  low: "var(--color-info)",
};

const SEVERITY_BG = {
  high: "color-mix(in srgb, var(--color-error) 8%, transparent)",
  medium: "color-mix(in srgb, var(--color-warning) 8%, transparent)",
  low: "color-mix(in srgb, var(--color-info) 8%, transparent)",
};

const DimensionCard = ({ dimension, theme }) => {
  const [expanded, setExpanded] = useState(dimension.status !== "pass");
  const config = STATUS_CONFIG[dimension.status] || STATUS_CONFIG.pass;
  const Icon = config.icon;
  const issues = dimension.issues || [];

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 transition-all hover:bg-[var(--color-bg-tertiary)]"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <Icon size={15} style={{ color: config.color }} />
          </div>
          <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{dimension.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {issues.length > 0 && (
            <span className="text-2xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: config.bg, color: config.color }}>
              {issues.length} 个问题
            </span>
          )}
          {issues.length === 0 && dimension.status === "pass" && (
            <span className="text-2xs" style={{ color: config.color }}>通过</span>
          )}
          <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} style={{ color: "var(--color-text-muted)" }} />
        </div>
      </button>

      {expanded && issues.length > 0 && (
        <div className="px-4 pb-3 space-y-2 animate-in fade-in duration-200" style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
          <div className="pt-2" />
          {issues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
              <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.medium }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-primary)" }}>{issue.description}</p>
                <div className="flex items-center gap-2 mt-1">
                  {issue.location && (
                    <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>📍 {issue.location}</span>
                  )}
                  {issue.severity && (
                    <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: SEVERITY_BG[issue.severity] || SEVERITY_BG.medium, color: SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.medium }}>
                      {issue.severity === "high" ? "严重" : issue.severity === "medium" ? "中等" : "轻微"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {expanded && issues.length === 0 && (
        <div className="px-4 pb-3 pt-2 text-xs animate-in fade-in duration-200" style={{ color: "var(--color-text-muted)", borderTop: "1px solid var(--color-border-secondary)" }}>
          未发现问题 ✓
        </div>
      )}
    </div>
  );
};

const LogicCheckResult = ({ data, theme }) => {
  if (!data) return null;

  const dimensions = data.dimensions || [];
  const totalIssues = data.totalIssues || dimensions.reduce((sum, d) => sum + (d.issues?.length || 0), 0);
  const passCount = dimensions.filter(d => d.status === "pass").length;

  return (
    <div className="space-y-4">
      {/* 总览卡片 */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} style={{ color: theme.colors.accentPrimary }} />
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>逻辑一致性检查报告</span>
        </div>

        {/* 统计 */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <CheckCircle size={14} style={{ color: "var(--color-success)" }} />
            <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>{passCount}/{dimensions.length} 维度通过</span>
          </div>
          {totalIssues > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={14} style={{ color: "var(--color-warning)" }} />
              <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>{totalIssues} 个问题待修复</span>
            </div>
          )}
        </div>

        {/* 进度条 */}
        <div className="h-2 rounded-full overflow-hidden flex" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
          {dimensions.map((d, i) => (
            <div key={i} className="h-full" style={{
              width: `${100 / dimensions.length}%`,
              backgroundColor: (STATUS_CONFIG[d.status] || STATUS_CONFIG.pass).color,
              opacity: 0.7,
            }} />
          ))}
        </div>

        {/* 总结 */}
        {data.summary && (
          <p className="text-xs leading-relaxed mt-3" style={{ color: "var(--color-text-muted)" }}>{data.summary}</p>
        )}
      </div>

      {/* 各维度详情 */}
      <div className="space-y-2">
        {dimensions.map((dim, i) => (
          <DimensionCard key={i} dimension={dim} theme={theme} />
        ))}
      </div>
    </div>
  );
};

export default LogicCheckResult;
